import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, AlertTriangle, Scale, MapPin, ChevronRight } from 'lucide-react';

export const Legal = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const legalContent = {
    en: {
      title: "LEGAL NOTICE",
      subtitle: "Know your rights. Know the law.",
      intro: "Self-defense is a fundamental right. However, regulations vary by country. Read carefully before purchasing.",
      sections: [
        {
          title: "FRANCE",
          flag: "🇫🇷",
          items: [
            { label: "Contact Stun Devices", status: "legal", note: "Category D - Adult sale only (18+)" },
            { label: "Projectile Tasers", status: "restricted", note: "Category B - Strictly regulated" },
            { label: "Pepper Spray", status: "legal", note: "Category D - Max 100ml" },
            { label: "Alarms & Kubatons", status: "free", note: "Not classified as weapons" },
            { label: "Carry & Transport", status: "warning", note: "Strictly regulated - legitimate reason required" },
            { label: "Usage", status: "warning", note: "ONLY in legitimate self-defense (necessity, immediacy, proportionality)" }
          ]
        },
        {
          title: "SPAIN",
          flag: "🇪🇸",
          items: [
            { label: "Stun Devices", status: "restricted", note: "Generally prohibited without authorization" },
            { label: "Pepper Spray", status: "legal", note: "Legal for personal defense" },
            { label: "Alarms & Kubatons", status: "free", note: "Generally legal" },
            { label: "Note", status: "warning", note: "Laws vary by autonomous community - verify local regulations" }
          ]
        }
      ],
      conditions: [
        "Products sold exclusively for legitimate self-defense purposes",
        "Buyer must be of legal age (18+)",
        "Buyer responsible for knowing and complying with local laws",
        "Misuse may result in criminal prosecution",
        "Fight like a GYAL not responsible for illegal use"
      ],
      warning: "This information is for guidance only and does not constitute legal advice. Laws change frequently. Consult official sources or a legal professional."
    },
    es: {
      title: "AVISO LEGAL",
      subtitle: "Conoce tus derechos. Conoce la ley.",
      intro: "La defensa personal es un derecho fundamental. Sin embargo, las regulaciones varían por país. Lee atentamente antes de comprar.",
      sections: [
        {
          title: "FRANCIA",
          flag: "🇫🇷",
          items: [
            { label: "Dispositivos Eléctricos de Contacto", status: "legal", note: "Categoría D - Venta solo adultos (18+)" },
            { label: "Taser con Proyectiles", status: "restricted", note: "Categoría B - Estrictamente regulado" },
            { label: "Spray de Pimienta", status: "legal", note: "Categoría D - Máx 100ml" },
            { label: "Alarmas y Kubatons", status: "free", note: "No clasificados como armas" },
            { label: "Porte y Transporte", status: "warning", note: "Estrictamente regulado - razón legítima requerida" },
            { label: "Uso", status: "warning", note: "SOLO en legítima defensa (necesidad, inmediatez, proporcionalidad)" }
          ]
        },
        {
          title: "ESPAÑA",
          flag: "🇪🇸",
          items: [
            { label: "Dispositivos Eléctricos", status: "restricted", note: "Generalmente prohibidos sin autorización" },
            { label: "Spray de Pimienta", status: "legal", note: "Legal para defensa personal" },
            { label: "Alarmas y Kubatons", status: "free", note: "Generalmente legales" },
            { label: "Nota", status: "warning", note: "Las leyes varían por comunidad autónoma - verifica regulaciones locales" }
          ]
        }
      ],
      conditions: [
        "Productos vendidos exclusivamente para legítima defensa",
        "El comprador debe ser mayor de edad (18+)",
        "El comprador es responsable de conocer y cumplir las leyes locales",
        "El uso indebido puede resultar en procesamiento penal",
        "Fight like a GYAL no se hace responsable del uso ilegal"
      ],
      warning: "Esta información es solo orientativa y no constituye asesoramiento legal. Las leyes cambian frecuentemente. Consulta fuentes oficiales o un profesional legal."
    },
    fr: {
      title: "MENTIONS LÉGALES",
      subtitle: "Connaissez vos droits. Connaissez la loi.",
      intro: "L'autodéfense est un droit fondamental. Cependant, les réglementations varient selon les pays. Lisez attentivement avant d'acheter.",
      sections: [
        {
          title: "FRANCE",
          flag: "🇫🇷",
          items: [
            { label: "Dispositifs Électriques de Contact", status: "legal", note: "Catégorie D - Vente aux majeurs uniquement (18+)" },
            { label: "Taser à Sondes", status: "restricted", note: "Catégorie B - Strictement encadré" },
            { label: "Spray au Poivre", status: "legal", note: "Catégorie D - Max 100ml" },
            { label: "Alarmes et Kubatons", status: "free", note: "Non classés comme armes" },
            { label: "Port et Transport", status: "warning", note: "Strictement encadrés - motif légitime requis" },
            { label: "Usage", status: "warning", note: "UNIQUEMENT en légitime défense (nécessité, immédiateté, proportionnalité)" }
          ]
        },
        {
          title: "ESPAGNE",
          flag: "🇪🇸",
          items: [
            { label: "Dispositifs Électriques", status: "restricted", note: "Généralement interdits sans autorisation" },
            { label: "Spray au Poivre", status: "legal", note: "Légal pour la défense personnelle" },
            { label: "Alarmes et Kubatons", status: "free", note: "Généralement légaux" },
            { label: "Note", status: "warning", note: "Les lois varient selon la communauté autonome - vérifiez les réglementations locales" }
          ]
        }
      ],
      conditions: [
        "Produits vendus exclusivement à des fins de légitime défense",
        "L'acheteur doit être majeur (18+)",
        "L'acheteur est responsable de connaître et respecter les lois locales",
        "L'utilisation abusive peut entraîner des poursuites pénales",
        "Fight like a GYAL n'est pas responsable de l'utilisation illégale"
      ],
      warning: "Ces informations sont à titre indicatif uniquement et ne constituent pas un conseil juridique. Les lois changent fréquemment. Consultez les sources officielles ou un professionnel du droit."
    }
  };

  const content = legalContent[lang] || legalContent.en;

  const getStatusStyle = (status) => {
    switch (status) {
      case 'legal':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'restricted':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'free':
        return 'bg-[#00FFFF]/20 text-[#00FFFF] border-[#00FFFF]/30';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-white/10 text-white border-white/20';
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="legal-page">
      {/* Hero */}
      <div className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF0099]/10 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Scale className="w-16 h-16 text-[#FF0099] mx-auto mb-6" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-black mb-4 tracking-tight">
            {content.title}
          </h1>
          <p className="text-[#FAFF00] font-accent text-lg" style={{ transform: 'rotate(-1deg)' }}>
            {content.subtitle}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Intro */}
        <div className="mb-12 p-6 border-l-4 border-[#FF0099] bg-[#1A1A1A]">
          <p className="text-[#A3A3A3] text-lg leading-relaxed">
            {content.intro}
          </p>
        </div>

        {/* Country Sections */}
        <div className="space-y-8 mb-12">
          {content.sections.map((section, index) => (
            <div key={index} className="bg-[#0a0a0a] border border-[#333333] overflow-hidden">
              {/* Country Header */}
              <div className="bg-[#1A1A1A] p-4 border-b border-[#333333] flex items-center gap-3">
                <span className="text-3xl">{section.flag}</span>
                <h2 className="font-heading text-xl text-white font-bold uppercase tracking-wider">
                  {section.title}
                </h2>
              </div>
              
              {/* Items */}
              <div className="divide-y divide-[#333333]/50">
                {section.items.map((item, i) => (
                  <div key={i} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-[#1A1A1A]/50 transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <ChevronRight className="w-4 h-4 text-[#FF0099] shrink-0" />
                      <span className="text-white font-bold text-sm">{item.label}</span>
                    </div>
                    <div className={`px-3 py-1 border text-xs font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                      {item.note}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* General Conditions */}
        <div className="mb-12">
          <h3 className="font-heading text-lg text-white font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FF0099]" />
            {lang === 'es' ? 'Condiciones Generales' : lang === 'fr' ? 'Conditions Générales' : 'General Conditions'}
          </h3>
          <div className="bg-[#1A1A1A] border border-[#333333] p-6">
            <ul className="space-y-3">
              {content.conditions.map((condition, i) => (
                <li key={i} className="flex items-start gap-3 text-[#A3A3A3] text-sm">
                  <span className="text-[#FF0099] mt-1">✓</span>
                  {condition}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Warning */}
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0099] to-[#FAFF00] opacity-20 blur-sm" />
          <div className="relative bg-[#0a0a0a] border border-[#FF0099]/50 p-6 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-[#FAFF00] shrink-0 mt-1" />
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              {content.warning}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
